/*
  Edge Mapping Utilities

  This module handles conversion between API edges and document edges,
  including workflow outcome options transformation.
*/

import { v4 as uuidv4 } from 'uuid';
import type { ApiEdge, Edge as DocEdge } from '../../types';
import type { InvestigationType } from '../../types/shared';

/**
 * Key mappings for workflow outcome options
 * Maps between camelCase (backend) and snake_case (frontend)
 */
const WORKFLOW_OUTCOME_KEY_MAPPINGS = {
  opensAlert: 'opens_alert',
  investigationType: 'investigation_type',
  investigationError: 'investigation_error',
  opensInvestigation: 'opens_investigation',
  opens_alert: 'opens_alert',
  investigation_type: 'investigation_type',
  investigation_error: 'investigation_error',
  opens_investigation: 'opens_investigation',
  opens_application: 'opens_application',
} as const;

/**
 * Transform workflow outcome options from backend format to frontend format
 *
 * Backend sends these keys directly under config, we nest them under workflow_outcome_options
 * and convert camelCase to snake_case.
 *
 * @param config - The edge config object
 * @param investigationTypes - Available investigation types for expansion
 * @returns Transformed workflow outcome options
 */
function transformWorkflowOutcomeOptions(
  config: Record<string, any>,
  investigationTypes?: InvestigationType[],
): Record<string, unknown> {
  const workflowOutcomeOptions: Record<string, unknown> = {};

  Object.entries(WORKFLOW_OUTCOME_KEY_MAPPINGS).forEach(([oldKey, newKey]) => {
    if (config[oldKey] !== undefined) {
      if ((oldKey === 'investigationType' || oldKey === 'investigation_type') && investigationTypes) {
        // Expand investigation_type string to full investigation type object
        if (typeof config[oldKey] === 'string') {
          const investigationTypeObj = investigationTypes.find(
            invType => invType.name === config[oldKey],
          );
          workflowOutcomeOptions[newKey] = investigationTypeObj || config[oldKey]; // fallback to string if not found
        } else {
          workflowOutcomeOptions[newKey] = config[oldKey];
        }
      } else {
        workflowOutcomeOptions[newKey] = config[oldKey];
      }
    }
  });

  return workflowOutcomeOptions;
}

/**
 * Converts an API edge to document model format
 *
 * This function handles:
 * - workflow_outcome_options transformation from backend format
 * - Precedence assignment
 * - Field normalization
 * - UUID generation for edge IDs
 *
 * @param edge - The API edge to convert
 * @param idx - The index of the edge (unused, kept for compatibility)
 * @param investigationTypes - Available investigation types for expansion
 * @returns The normalized document edge
 */
export function mapApiEdgeToDocEdge(
  edge: ApiEdge,
  idx: number,
  investigationTypes?: InvestigationType[],
): DocEdge {
  const config = edge.config || {};

  // Transform workflow outcome options from backend format
  const workflowOutcomeOptions = transformWorkflowOutcomeOptions(config, investigationTypes);

  // Create the final config, removing the mapped keys from top level if they exist
  const finalConfig = { ...config };
  Object.keys(WORKFLOW_OUTCOME_KEY_MAPPINGS).forEach(key => {
    delete finalConfig[key];
  });

  // Add workflow_outcome_options if any keys were found
  if (Object.keys(workflowOutcomeOptions).length > 0) {
    finalConfig.workflow_outcome_options = workflowOutcomeOptions;
  }

  return {
    id: uuidv4(),
    from: { node: edge.source },
    to: { node: edge.target },
    condition: edge.condition ?? null,
    condition_id: null,
    precedence: edge.precedence ?? undefined,
    config: finalConfig,
  };
}

